import React, { useEffect, useState } from 'react'
import { Button, Checkbox} from '@material-ui/core'
import { observer } from 'mobx-react-lite';
import { DataGrid, GridColDef, GridEditCellPropsParams } from '@material-ui/data-grid';
import StepConfirmation, { ConfirmationDialogProps, defaultConfirmationDialogProps } from '../../controls/StepConfirmation';
import AquantWarningIcon from '../../../assets/aq-warning.svg'
import { CachedItem } from '../models';
import { Stack, Typography } from '@mui/material';
import { SelectionItem, SIAutocomplete } from '../../controls/Autocomplete';
import CopyTooltip from '../components/CopyTooltip';
import { CopilotStore } from '../CopilotStore';


export const CopilotCaching = observer(({ store }: { store: CopilotStore }) => {
    const {
        selectedCustomer: customer,
        models,
        selectedModel,
        setSelectedModel,
        deleteCacheItems
    } = store

    const [isDoingAction, setIsDoingAction] = useState<boolean>(false)
    const [cachedItems, setCachedItems] = React.useState<Array<CachedItem>>([])
    const [deleteSelectedItems, setDeleteSelectedItems] = React.useState<Array<string>>([])
    const [changedItems, setChangedItems] = React.useState(new Set())
    const [isFetchingFiles, setIsFetchingFiles] = useState<boolean>(false)
    const [deleteDialogProps, setDeleteDialogProps] = useState<ConfirmationDialogProps | undefined>(undefined)

    const fetchFiles = () => {
        if (customer && selectedModel?.value){
            setIsFetchingFiles(true)
            const cacheItems = store.fetchCacheItems(customer.name, selectedModel?.value!)
    
            cacheItems.then((result) => {
                console.log("got: " + result)
                setCachedItems(result)
            }).then(() => setIsFetchingFiles(false))
        }
    }

    useEffect(() => {
        fetchFiles()
    }, [customer, selectedModel])


    const onCheckChanged = (value:boolean, id:string) => {
        if (value === true){
            setDeleteSelectedItems(prevItems => [...prevItems, id]);
        }
        else{
            setDeleteSelectedItems(prevItems => prevItems.filter(prevItem => prevItem !== id));
        }
    }

    const columns: GridColDef[] = [
        {
            field: ' ',
            headerName: '',
            width: 62,
            renderCell: (params) => (
              <Checkbox
                checked={params.value as boolean}
                onChange={(event) => { onCheckChanged(event.target.checked, String(params.id))}}
              />
            ),
        },
        {
            field: 'id',
            headerName: 'Id',
            flex: 3,
            editable: false,
            hide:true
        },
        {
            field: 'text',
            headerName: 'Text',
            width: 350,
            flex: 4,
            editable: true,
        },
        {
            field: 'question',
            headerName: 'Question',
            flex: 2,
            width: 90,
            editable: false,
            renderCell: (params: any) => <CopyTooltip text={params.value}/>
        }
    ];

    const getDeleteConfirmationDialog = (onDelete: () => void) => {
        const saveConfirmProps: ConfirmationDialogProps = {
            ...defaultConfirmationDialogProps,
            title: 'Confirm Delete?',
            content: <>
                <div style={{ display: 'flex', marginBottom: 15 }} >
                    <img src={AquantWarningIcon} alt='warning-icon' style={{ marginRight: 5 }} />
                    <Typography variant='subtitle2'>
                        You are about to delete a cached item.
                    </Typography>
                </div>
                <Typography variant='body1' style={{marginTop: 15}}>
                    Are you sure you want to proceed?
                </Typography>
            </>,
            onYes: () => { onDelete() },
            yesLabel: 'Yes',
            closeLabel: 'No',
        }

        return saveConfirmProps
    }

    const onSave = () => {
        setIsDoingAction(true)
        store.editCacheItems(customer!.name, selectedModel?.value!, Array.from(changedItems) as Array<CachedItem>)
            .then(fetchFiles)
            .then(() => setIsDoingAction(false))
    }

    const onDelete = () => {
        setIsDoingAction(true)
        deleteCacheItems(customer!.name, selectedModel?.value!, deleteSelectedItems)
            .then(fetchFiles)
            .then(() => setIsDoingAction(false))
    }

    const handleEditCellChange = (params: GridEditCellPropsParams) => {
        const item = cachedItems.find(element => element.id === params.id)
        if (item != undefined && params.field === 'text'){
            const tempValue = String(params.props.value)
            if (item.text !== tempValue){
                item.text = String(params.props.value)
                setChangedItems(prev => new Set(prev.add(item)))
            }
        }
    }

    return (
        <Stack gap={2} padding={0.5} height='100%'>
            <Stack direction={"row"} justifyContent={"space-between"}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography style={{fontWeight: 500}}>Model:</Typography>
                        <SIAutocomplete
                            items={models.map(tc => ({ value: tc.rowid, label: tc.value }))}
                            initial_selection={selectedModel ? { value: selectedModel.rowid, label: selectedModel.value } : undefined} 
                            onSelect={(item?: SelectionItem) => setSelectedModel(item?.value)}
                            style={{ width: '500px' }}
                            loadProgress={() => true}
                            placeholder='Select Model'
                            variant='standard'
                        />
                </Stack>
                <Stack direction={"row"} spacing={2}>
                    <Button variant='outlined'
                        style={{ width: '150px' }}
                        onClick={() => onSave()}
                        disabled = {changedItems.size === 0}
                    >Save</Button>
                    <Button variant='outlined'
                        style={{ width: '150px' }}
                        disabled = {deleteSelectedItems.length === 0}
                        onClick={() => setDeleteDialogProps(getDeleteConfirmationDialog(() => onDelete()))}
                    >Delete</Button>
                </Stack>
            </Stack>

            {cachedItems && <DataGrid
                rows={cachedItems}
                columns={columns}
                pageSize={9}
                disableSelectionOnClick
                getRowId={row => row.id}
                onEditCellChange={handleEditCellChange}
                loading={isFetchingFiles || isDoingAction}
            />}

            {deleteDialogProps && <StepConfirmation
                confirmationDialogs={[deleteDialogProps]}
                finish={() => { setDeleteDialogProps(undefined)}}
                cancel={() => setDeleteDialogProps(undefined)}
                isValid={true}
            />}
        </Stack>
    );
})